import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { login as apiLogin } from "../api/auth";
import { apiFetch, clearToken, getToken } from "../api/client";
import { getLocale } from "../i18n";
import { resolveAdminErrorMessage } from "../lib/admin-error-messages";

interface User {
  id: string;
  email: string;
  display_name: string;
  custom_id: string | null;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthProvider(): AuthContextValue {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = getToken();
    const savedUser = localStorage.getItem("hashee_admin_user");
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser) as User);
      } catch {
        clearToken();
        localStorage.removeItem("hashee_admin_user");
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setError(null);
      setIsLoading(true);
      try {
        const data = await apiLogin({ email, password });
        localStorage.setItem("hashee_admin_user", JSON.stringify(data.user));
        setUser(data.user);
      } catch (err: unknown) {
        const apiErr = err as { errorCode?: string; message?: string };
        if (apiErr.errorCode) {
          setError(resolveAdminErrorMessage(apiErr.errorCode, getLocale()));
        } else {
          setError(apiErr.message || "Login failed. Please try again.");
        }
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logout = useCallback((): void => {
    void apiFetch("/admin-auth/logout", { method: "POST" }).catch(
      () => undefined,
    );
    clearToken();
    localStorage.removeItem("hashee_admin_user");
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return useMemo(
    () => ({
      isAuthenticated: user !== null,
      isLoading,
      user,
      error,
      login,
      logout,
      clearError,
    }),
    [user, isLoading, error, login, logout, clearError],
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
